/**
 * Files/Media Manager API Endpoints
 * API service for user file management
 */

import { ApiService } from '../apiService'
import type {
  UserFileDto,
  UserFileDetailDto,
  MediaFolderDto,
  MediaFolderTreeDto,
  UserFilePagedResult,
  StorageQuotaDto,
  MediaFileUsageDto,
  GetFilesParams,
  CreateFolderRequest,
  UpdateFolderRequest,
  UpdateFileRequest,
  MoveFilesRequest,
  BulkOperationResultDto,
  UploadResultDto,
  FileCategory,
} from '../types/files'

export const filesApi = {
  // ===================
  // UPLOAD
  // ===================

  /** Upload a file to the user's media library */
  uploadFile: (
    file: File,
    options?: {
      folderId?: string
      category?: FileCategory
      displayName?: string
      description?: string
      tags?: string[]
    }
  ) => {
    const formData = new FormData()
    formData.append('file', file)
    if (options?.folderId) formData.append('folderId', options.folderId)
    if (options?.category !== undefined) formData.append('category', String(options.category))
    if (options?.displayName) formData.append('displayName', options.displayName)
    if (options?.description) formData.append('description', options.description)
    if (options?.tags?.length) formData.append('tags', options.tags.join(','))
    return ApiService.upload<UploadResultDto>('/api/me/files/upload', formData)
  },
  // ===================
  // FILES
  // ===================

  /** Get user's files with optional filters */
  getFiles: (params?: GetFilesParams) =>
    ApiService.get<UserFilePagedResult>('/api/me/files', params),

  /** Get single file details */
  getFile: (fileId: string) =>
    ApiService.get<UserFileDetailDto>(`/api/me/files/${fileId}`),

  /** Get file usages */
  getFileUsages: (fileId: string) =>
    ApiService.get<MediaFileUsageDto[]>(`/api/me/files/${fileId}/usages`),

  /** Update file metadata */
  updateFile: (fileId: string, data: UpdateFileRequest) =>
    ApiService.patch<UserFileDto>(`/api/me/files/${fileId}`, data),

  /** Delete a file */
  deleteFile: (fileId: string, force?: boolean) => {
    const url = force ? `/api/me/files/${fileId}?force=true` : `/api/me/files/${fileId}`
    return ApiService.delete<void>(url)
  },

  /** Toggle file favorite status */
  toggleFavorite: (fileId: string) =>
    ApiService.post<void>(`/api/me/files/${fileId}/favorite`),

  /** Move files to a folder */
  moveFiles: (data: MoveFilesRequest) =>
    ApiService.post<BulkOperationResultDto>('/api/me/files/move', data),

  /** Get files for an entity */
  getFilesForEntity: (entityType: string, entityId: string) =>
    ApiService.get<UserFileDto[]>(`/api/me/files/entity/${entityType}/${entityId}`),

  /** Select/link a file to an entity */
  selectFile: (data: { fileId: string; entityType: string; entityId: string }) =>
    ApiService.post<{ fileUrl: string }>('/api/me/files/select', data),

  /** Delete a file usage */
  deleteUsage: (usageId: string) =>
    ApiService.delete<void>(`/api/me/files/usages/${usageId}`),

  // ===================
  // FOLDERS
  // ===================

  /** Get all user's folders (flat list) */
  getFolders: () =>
    ApiService.get<MediaFolderDto[]>('/api/me/files/folders'),

  /** Get folder tree structure */
  getFolderTree: () =>
    ApiService.get<MediaFolderTreeDto[]>('/api/me/files/folders/tree'),

  /** Create a new folder */
  createFolder: (data: CreateFolderRequest) =>
    ApiService.post<MediaFolderDto>('/api/me/files/folders', data),

  /** Update folder */
  updateFolder: (folderId: string, data: UpdateFolderRequest) =>
    ApiService.patch<MediaFolderDto>(`/api/me/files/folders/${folderId}`, data),

  /** Delete folder */
  deleteFolder: (folderId: string) =>
    ApiService.delete<void>(`/api/me/files/folders/${folderId}`),

  // ===================
  // QUOTA
  // ===================

  /** Get storage quota information */
  getQuota: () =>
    ApiService.get<StorageQuotaDto>('/api/me/files/quota'),
}
